/* eslint-disable @typescript-eslint/no-shadow */
import MapView from '@arcgis/core/views/MapView';
import { debounce } from 'lodash';
import { BasemapOption, ESRI_GOOGLE_TILE_TYPE } from '../../../layer-picker/basemaps';
import { MapPlugin } from '../arcgis-types';
import { LayerSettingsHandler } from './utils/layer-settings-reference-handler';
import GoogleBasemapBuilder, { GoogleBasemap } from './utils/google-basemap-builder';
import { variation } from '../../../launch-darkly';
import arcGisFactory from '../../../arcGIS/arcgis-factory';

const FEATURE_FLAG = 'google-maps-tiles';
const SPATIAL_REFERENCE_WKID = 102100; // Same as 3857 - See https://support.esri.com/en-us/knowledge-base/why-does-arcgis-online-use-a-deprecated-spatial-referen-000013950
export const DEBOUNCE_INTERVAL = 500;

export const enableGoogleBasemapTiles = () => variation(FEATURE_FLAG, false)
  || localStorage.getItem(FEATURE_FLAG) === 'true';

let googleBasemapBuilder: GoogleBasemap;

/**
 * The Basemap plugin is responsible for the `mapView.map.basemap`
 *
 */
const Basemap = (
  layerSettingsReferenceHandler: LayerSettingsHandler,
  googleApiKey: string,
  testableBuilder?: GoogleBasemap,
): MapPlugin => {
  googleBasemapBuilder = googleBasemapBuilder ?? testableBuilder ?? GoogleBasemapBuilder(googleApiKey);

  const updateBasemap = async (mapView: MapView, activeBasemap: BasemapOption) => {
    if (!activeBasemap || mapView?.destroyed) {
      return;
    }
    if (enableGoogleBasemapTiles() && activeBasemap.type === ESRI_GOOGLE_TILE_TYPE) {
      const googleBasemap = await googleBasemapBuilder.buildBasemap(activeBasemap.id, mapView);
      if (googleBasemap.destroyed) {
        return;
      }
      mapView.map?.set('basemap', googleBasemap);
    } else {
      mapView.map?.set('basemap', activeBasemap.esriMapTypeId);
    }
    if (activeBasemap.id === 'none' || activeBasemap.type === ESRI_GOOGLE_TILE_TYPE) {
      // If we load an xyz tile option initially, we'll be using the wrong spatial reference.
      // This ensures we use the same wkid as other basemaps.
      mapView.set('spatialReference', arcGisFactory().newSpatialReference({ wkid: SPATIAL_REFERENCE_WKID }));
    }
  };

  const debounceUpdateBasemap = debounce(
    (mapView: MapView, activeBasemap: BasemapOption) => {
      updateBasemap(mapView, activeBasemap);
    },
    DEBOUNCE_INTERVAL,
    { leading: false, trailing: true },
  );

  return {
    name: 'basemap',
    initMap: (mapView: MapView) => {
      if (!mapView || mapView.destroyed) {
        return;
      }

      let previousExtent = mapView.extent;
      const { activeBasemap } = layerSettingsReferenceHandler.getInstance();

      if (activeBasemap) {
        updateBasemap(mapView, activeBasemap);
      }

      mapView.watch('updating', async () => {
        const { extent } = mapView;
        const { activeBasemap } = layerSettingsReferenceHandler.getInstance();
        if (mapView?.destroyed || !extent || extent.equals(previousExtent)) {
          return;
        }
        previousExtent = extent;
        debounceUpdateBasemap(mapView, activeBasemap);
      });
    },
    extendMap: async (mapView: MapView) => {
      if (!layerSettingsReferenceHandler.getInstance()) {
        return;
      }

      layerSettingsReferenceHandler.registerListener('change', () => {
        const { activeBasemap } = layerSettingsReferenceHandler.getInstance();
        if (!activeBasemap || !mapView) {
          return;
        }

        const needNewGoogleTilesSession = activeBasemap.type === ESRI_GOOGLE_TILE_TYPE;
        const currentBasemapId = mapView.map?.basemap?.id;
        if (needNewGoogleTilesSession || currentBasemapId !== activeBasemap.id) {
          debounceUpdateBasemap(mapView, activeBasemap);
        }
      });
    },
  };
};

export default Basemap;
